import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { Environment } from "../../environment";
import { AccountService } from "./account.service";

interface BrowserSpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: { transcript: string };
}

interface BrowserSpeechRecognitionResultList {
    readonly length: number;
    [index: number]: BrowserSpeechRecognitionResult;
}

interface BrowserSpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: BrowserSpeechRecognitionResultList;
}

interface BrowserSpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message?: string;
}

interface BrowserSpeechRecognition extends EventTarget {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    maxAlternatives: number;
    onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
    onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface BrowserSpeechRecognitionConstructor {
    new(): BrowserSpeechRecognition;
}

interface BrowserWindow extends Window {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
}

interface ActiveStreamingPlayback {
    audioElement: HTMLAudioElement;
    mediaSource: MediaSource;
    sourceBuffer?: SourceBuffer;
    abortController?: AbortController;
    streamCompleted: boolean;
}

@Injectable({
    providedIn: "root",
})
export class VoiceService {
    private recognition?: BrowserSpeechRecognition;
    private recordingSubject?: Subject<string>;
    private shouldKeepRecording = false;
    private recordingLanguage = "en-US";
    private committedTranscript = "";
    private sessionResults: Array<{ transcript: string; isFinal: boolean }> = [];
    private lastEmittedTranscript = "";
    private restartTimeoutId?: number;
    private activeReadTextResolver?: () => void;
    private activePlaybackAbortController?: AbortController;
    private activeStreamingPlayback?: ActiveStreamingPlayback;
    private playbackRequestId = 0;
    private readonly restartDelayMs = 100;

    public constructor(private accountService: AccountService) {
    }

    public startRecording(language = "en-US"): Observable<string> {
        if (this.recordingSubject && this.shouldKeepRecording) {
            return this.recordingSubject.asObservable();
        }

        const speechRecognitionConstructor = this.getSpeechRecognitionConstructor();
        if (!speechRecognitionConstructor) {
            return new Observable<string>((observer) => {
                observer.error(new Error("Speech recognition is not supported by this browser."));
            });
        }

        this.forceStopCurrentRecognition();

        this.shouldKeepRecording = true;
        this.recordingLanguage = language;
        this.committedTranscript = "";
        this.resetSessionResults();
        this.lastEmittedTranscript = "";
        this.recordingSubject = new Subject<string>();

        try {
            this.startRecognitionSession();
        } catch (error) {
            this.emitRecordingError(this.asError(error, "Unable to start speech recognition."));
        }

        return this.recordingSubject.asObservable();
    }

    public stopRecording(): void {
        this.shouldKeepRecording = false;

        if (!this.recognition) {
            this.completeRecording();
            return;
        }

        try {
            this.recognition.stop();
        } catch {
            this.completeRecording();
        }
    }

    public readText(text: string): Promise<void> {
        if (typeof window === "undefined") {
            return Promise.resolve();
        }

        const normalizedText = text.trim();
        if (!normalizedText) {
            return Promise.resolve();
        }

        const requestId = ++this.playbackRequestId;

        this.cancelActivePlayback();
        this.resolveActiveReadText();

        return new Promise<void>((resolve) => {
            this.activeReadTextResolver = resolve;
            const abortController = new AbortController();
            this.activePlaybackAbortController = abortController;

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            const authToken = this.accountService.authToken;
            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }

            const finish = () => {
                if (requestId !== this.playbackRequestId) {
                    return;
                }

                this.clearActivePlayback();
                if (this.activeReadTextResolver === resolve) {
                    this.activeReadTextResolver = undefined;
                }

                resolve();
            };

            const fail = (error: unknown) => {
                console.error("TTS playback failed", error);
                finish();
            };

            void fetch(`${Environment.ApiUri}/ai/tts/stream`, {
                method: "POST",
                headers,
                body: JSON.stringify({ input: normalizedText }),
                signal: abortController.signal,
            }).then(async (response) => {
                if (!response.ok) {
                    throw new Error(`TTS request failed (${response.status} ${response.statusText}).`);
                }

                await this.playStreamingResponse(response, requestId, abortController.signal, finish);
            }).catch((error: unknown) => {
                if (abortController.signal.aborted) {
                    finish();
                    return;
                }

                fail(error);
            });
        });
    }

    public interruptRead(): void {
        this.playbackRequestId++;
        this.cancelActivePlayback();
        this.resolveActiveReadText();
    }

    private cancelActivePlayback(): void {
        if (this.activePlaybackAbortController && !this.activePlaybackAbortController.signal.aborted) {
            this.activePlaybackAbortController.abort();
        }

        this.clearActivePlayback();
    }

    private resolveActiveReadText(): void {
        if (!this.activeReadTextResolver) {
            return;
        }

        const resolve = this.activeReadTextResolver;
        this.activeReadTextResolver = undefined;
        resolve();
    }

    private clearActivePlayback(): void {
        this.activePlaybackAbortController = undefined;

        const playback = this.activeStreamingPlayback;
        this.activeStreamingPlayback = undefined;

        if (playback) {
            try {
                playback.audioElement.pause();
                playback.audioElement.src = "";
                URL.revokeObjectURL(playback.audioElement.src);
                playback.audioElement.remove();
            } catch {
                // Ignore cleanup errors.
            }

            if (playback.mediaSource && playback.mediaSource.readyState === "open") {
                try {
                    playback.mediaSource.endOfStream();
                } catch {
                    // Ignore endOfStream errors during cleanup.
                }
            }

            if (playback.abortController && !playback.abortController.signal.aborted) {
                playback.abortController.abort();
            }
        }
    }

    private async playStreamingResponse(
        response: Response,
        requestId: number,
        abortSignal: AbortSignal,
        finish: () => void,
    ): Promise<void> {
        if (!response.body) {
            throw new Error("TTS response did not include a readable stream.");
        }

        const audioElement = document.createElement("audio");
        audioElement.controls = false;
        audioElement.preload = "none";

        const mediaSource = new MediaSource();
        const objectUrl = URL.createObjectURL(mediaSource);
        audioElement.src = objectUrl;

        const abortController = new AbortController();

        this.activeStreamingPlayback = {
            audioElement,
            mediaSource,
            abortController,
            streamCompleted: false,
        };

        const playback = this.activeStreamingPlayback;
        const reader = response.body.getReader();
        let sourceBufferReady = true;

        const waitForSourceBufferReady = (sb: SourceBuffer): Promise<void> => {
            return new Promise<void>((resolve) => {
                if (!sb.updating) {
                    resolve();
                    return;
                }
                sb.onupdateend = () => resolve();
            });
        };

        mediaSource.addEventListener("sourceopen", async () => {
            try {
                const mimeType = this.detectAudioMimeType(response);
                let sourceBuffer: SourceBuffer;
                try {
                    sourceBuffer = mediaSource.addSourceBuffer(mimeType);
                } catch {
                    // Fallback to a more generic MIME type.
                    sourceBuffer = mediaSource.addSourceBuffer("audio/webm");
                }
                playback.sourceBuffer = sourceBuffer;
                sourceBuffer.mode = "sequence";

                sourceBufferReady = true;

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        playback.streamCompleted = true;
                        if (mediaSource.readyState === "open") {
                            mediaSource.endOfStream();
                        }
                        break;
                    }

                    if (requestId !== this.playbackRequestId || abortSignal.aborted || abortController.signal.aborted) {
                        reader.releaseLock();
                        finish();
                        return;
                    }

                    if (!value || !playback.sourceBuffer) {
                        continue;
                    }

                    try {
                        if (playback.sourceBuffer.updating) {
                            await waitForSourceBufferReady(playback.sourceBuffer);
                        }
                        playback.sourceBuffer.appendBuffer(value);
                    } catch (error: unknown) {
                        const message = error instanceof Error ? error.message : String(error);
                        if (message.includes("SourceBuffer") && message.includes("updating")) {
                            await waitForSourceBufferReady(playback.sourceBuffer);
                            try {
                                playback.sourceBuffer!.appendBuffer(value);
                            } catch {
                                // Ignore second append failure.
                            }
                        } else {
                            console.warn("SourceBuffer append error:", error);
                        }
                    }
                }
            } catch (error: unknown) {
                console.error("MSE playback error:", error);
                if (mediaSource.readyState === "open") {
                    mediaSource.endOfStream();
                }
            } finally {
                reader.releaseLock();
            }
        });

        audioElement.addEventListener("ended", () => {
            if (requestId === this.playbackRequestId && !abortSignal.aborted) {
                finish();
            }
        });

        audioElement.addEventListener("error", () => {
            // Log audio errors but don't fail the promise.
            window.console.warn("Audio element error:", audioElement.error);
        });

        try {
            await audioElement.play();
        } catch {
            // Autoplay may be blocked; the audio will play once the user interacts.
            window.console.log("Audio play blocked, waiting for user interaction.");
        }
    }

    private detectAudioMimeType(response: Response): string {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("audio/ogg")) {
            return "audio/ogg";
        }
        if (contentType.includes("audio/webm")) {
            return "audio/webm";
        }
        if (contentType.includes("audio/mpeg") || contentType.includes("audio/mp3")) {
            return "audio/mpeg";
        }
        if (contentType.includes("audio/mp4") || contentType.includes("audio/aac")) {
            return "audio/mp4";
        }
        return "audio/webm";
    }

    private completeRecording(): void {
        this.recordingSubject?.complete();
        this.cleanupState();
    }

    private emitRecordingError(error: Error): void {
        this.recordingSubject?.error(error);
        this.cleanupState();
    }

    private cleanupState(): void {
        this.shouldKeepRecording = false;
        this.recordingLanguage = "en-US";
        this.committedTranscript = "";
        this.resetSessionResults();
        this.lastEmittedTranscript = "";
        this.recordingSubject = undefined;
        this.forceStopCurrentRecognition();
    }

    private forceStopCurrentRecognition(): void {
        this.clearPendingRestart();

        if (!this.recognition) {
            return;
        }

        const recognition = this.recognition;
        this.recognition = undefined;

        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;

        try {
            recognition.abort();
        } catch {
            // Ignore cleanup errors from stale recognition instances.
        }
    }

    private startRecognitionSession(): void {
        const speechRecognitionConstructor = this.getSpeechRecognitionConstructor();
        if (!speechRecognitionConstructor) {
            throw new Error("Speech recognition is not supported by this browser.");
        }

        this.resetSessionResults();

        const recognition = new speechRecognitionConstructor();
        this.recognition = recognition;

        recognition.lang = "en-US";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            if (this.recognition !== recognition) {
                return;
            }

            this.syncSessionResults(event);
            this.emitCurrentTranscript();
        };

        recognition.onerror = (event) => {
            if (this.recognition !== recognition) {
                return;
            }

            if (!this.shouldKeepRecording && event.error === "aborted") {
                return;
            }

            if (this.shouldRecoverFromError(event.error)) {
                return;
            }

            const message = event.message ? `${event.error}: ${event.message}` : event.error;
            this.emitRecordingError(new Error(`Speech recognition failed (${message}).`));
        };

        recognition.onend = () => {
            if (this.recognition !== recognition) {
                return;
            }

            this.commitCurrentSessionTranscript();
            this.recognition = undefined;

            if (!this.shouldKeepRecording) {
                this.completeRecording();
                return;
            }

            this.scheduleRecognitionRestart();
        };

        recognition.start();
    }

    private scheduleRecognitionRestart(): void {
        this.clearPendingRestart();

        this.restartTimeoutId = window.setTimeout(() => {
            this.restartTimeoutId = undefined;

            if (!this.shouldKeepRecording || this.recognition) {
                return;
            }

            try {
                this.startRecognitionSession();
            } catch (error) {
                this.emitRecordingError(this.asError(error, "Unable to restart speech recognition."));
            }
        }, this.restartDelayMs);
    }

    private clearPendingRestart(): void {
        if (this.restartTimeoutId === undefined) {
            return;
        }

        window.clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = undefined;
    }

    private syncSessionResults(event: BrowserSpeechRecognitionEvent): void {
        const { resultIndex, results } = event;

        this.sessionResults.length = results.length;

        for (let index = resultIndex; index < results.length; index++) {
            const result = results[index];
            const transcriptSegment = this.normalizeTranscript(result?.[0]?.transcript ?? "");

            this.sessionResults[index] = {
                transcript: transcriptSegment,
                isFinal: Boolean(result?.isFinal),
            };
        }
    }

    private emitCurrentTranscript(): void {
        this.emitTranscript(this.buildCombinedTranscript(true));
    }

    private emitTranscript(transcript: string): void {
        const normalizedTranscript = this.normalizeTranscript(transcript);

        if (!normalizedTranscript || normalizedTranscript === this.lastEmittedTranscript) {
            return;
        }

        this.lastEmittedTranscript = normalizedTranscript;
        this.recordingSubject?.next(normalizedTranscript);
    }

    private commitCurrentSessionTranscript(): void {
        const currentSessionTranscript = this.buildSessionTranscript(true);

        this.committedTranscript = this.mergeSessionTranscript(this.committedTranscript, currentSessionTranscript);
        this.resetSessionResults();
        this.emitTranscript(this.committedTranscript);
    }

    private shouldRecoverFromError(error: string): boolean {
        return this.shouldKeepRecording && ["aborted", "network", "no-speech"].includes(error);
    }

    private buildCombinedTranscript(includeInterim: boolean): string {
        return this.mergeSessionTranscript(this.committedTranscript, this.buildSessionTranscript(includeInterim));
    }

    private buildSessionTranscript(includeInterim: boolean): string {
        let transcript = "";

        for (const result of this.sessionResults) {
            if (!result || (!result.isFinal && !includeInterim)) {
                continue;
            }

            transcript = this.mergeSessionTranscript(transcript, result.transcript);
        }

        return this.normalizeTranscript(transcript);
    }

    private mergeSessionTranscript(baseTranscript: string, nextTranscript: string): string {
        const normalizedBaseTranscript = this.normalizeTranscript(baseTranscript);
        const normalizedNextTranscript = this.normalizeTranscript(nextTranscript);

        if (!normalizedBaseTranscript) {
            return normalizedNextTranscript;
        }

        if (!normalizedNextTranscript) {
            return normalizedBaseTranscript;
        }

        if (normalizedBaseTranscript === normalizedNextTranscript) {
            return normalizedBaseTranscript;
        }

        if (normalizedNextTranscript.startsWith(`${normalizedBaseTranscript} `)) {
            return normalizedNextTranscript;
        }

        if (normalizedBaseTranscript.endsWith(` ${normalizedNextTranscript}`)) {
            return normalizedBaseTranscript;
        }

        const baseWords = normalizedBaseTranscript.split(" ");
        const nextWords = normalizedNextTranscript.split(" ");
        const lowerCaseBaseWords = baseWords.map((word) => word.toLowerCase());
        const lowerCaseNextWords = nextWords.map((word) => word.toLowerCase());
        const maxOverlap = Math.min(baseWords.length, nextWords.length);

        for (let overlapLength = maxOverlap; overlapLength > 0; overlapLength--) {
            const baseSuffix = lowerCaseBaseWords.slice(-overlapLength).join(" ");
            const nextPrefix = lowerCaseNextWords.slice(0, overlapLength).join(" ");

            if (baseSuffix === nextPrefix) {
                return [...baseWords, ...nextWords.slice(overlapLength)].join(" ");
            }
        }

        return `${normalizedBaseTranscript} ${normalizedNextTranscript}`;
    }

    private resetSessionResults(): void {
        this.sessionResults = [];
    }

    private normalizeTranscript(transcript: string): string {
        return transcript.replace(/\s+/g, " ").trim();
    }

    private getSpeechRecognitionConstructor() {
        if (typeof window === "undefined") {
            return undefined;
        }

        const browserWindow = window as BrowserWindow;
        return browserWindow.webkitSpeechRecognition ?? browserWindow.SpeechRecognition;
    }

    private asError(error: unknown, fallbackMessage: string): Error {
        if (error instanceof Error) {
            return error;
        }

        return new Error(fallbackMessage);
    }

}
